export default function ConfigApi({ makeRequest, buildRequest }) {
	function getConfig(path) {
		return buildRequest().then(function (req) {
			return makeRequest(req.get(`/api/Config/${path}`));
		});
	}

	function upsertConfig(path, payload, idField) {
		const body = { ...payload, id: payload?.[idField] ?? payload?.id };
		return buildRequest().then(function (req) {
			return makeRequest(req.post(`/api/Config/${path}`, body));
		});
	}

	function deleteConfig(path, id) {
		return buildRequest().then(function (req) {
			return makeRequest(req.delete(`/api/Config/${path}/${id}`));
		});
	}

	function getIntents() {
		return getConfig('intents');
	}

	function upsertIntent(payload) {
		return upsertConfig('intents', payload, 'intentId');
	}

	function deleteIntent(id) {
		return deleteConfig('intents', id);
	}

	function getSources() {
		return getConfig('sources');
	}

	function upsertSource(payload) {
		return upsertConfig('sources', payload, 'sourceId');
	}

	function deleteSource(id) {
		return deleteConfig('sources', id);
	}

	function getTags() {
		return getConfig('tags');
	}

	function upsertTag(payload) {
		return upsertConfig('tags', payload, 'tagId');
	}

	function deleteTag(id) {
		return deleteConfig('tags', id);
	}

	function getReferralSources() {
		return getConfig('referral-sources');
	}

	function upsertReferralSource(payload) {
		return upsertConfig('referral-sources', payload, 'sourceId');
	}

	function deleteReferralSource(id) {
		return deleteConfig('referral-sources', id);
	}

	function getOnHoldReasons() {
		return getConfig('onhold-reasons');
	}

	function upsertOnHoldReason(payload) {
		return upsertConfig('onhold-reasons', payload, 'onHoldReasonId');
	}

	function deleteOnHoldReason(id) {
		return deleteConfig('onhold-reasons', id);
	}

	function getStatuses() {
		return getConfig('statuses');
	}

	function upsertStatus(payload) {
		return upsertConfig('statuses', payload, 'statusId');
	}

	function deleteStatus(id) {
		return deleteConfig('statuses', id);
	}

	function getChildAges() {
		return getConfig('child-ages');
	}

	function upsertChildAge(payload) {
		return upsertConfig('child-ages', payload, 'childAgeId');
	}

	function deleteChildAge(id) {
		return deleteConfig('child-ages', id);
	}

	function getSessions() {
		return getConfig('sessions');
	}

	function upsertSession(payload) {
		return upsertConfig('sessions', payload, 'sessionId');
	}

	function deleteSession(id) {
		return deleteConfig('sessions', id);
	}

	function getRelationships() {
		return getConfig('relationships');
	}

	function upsertRelationship(payload) {
		return upsertConfig('relationships', payload, 'relationshipId');
	}

	function deleteRelationship(id) {
		return deleteConfig('relationships', id);
	}

	function getCommunicationTypes() {
		return getConfig('communication-types');
	}

	function upsertCommunicationType(payload) {
		return upsertConfig('communication-types', payload, 'communicationTypeId');
	}

	function deleteCommunicationType(id) {
		return deleteConfig('communication-types', id);
	}

	function getNoteTypes() {
		return getConfig('note-types');
	}


	function upsertNoteType(payload) {
		return upsertConfig('note-types', payload, 'noteTypeId');
	}

	function deleteNoteType(id) {
		return deleteConfig('note-types', id);
	}

	function getCancellationReasons(type) {
		return buildRequest().then(function (req) {
			return makeRequest(req.get('/api/Config/cancellation-reasons', {
				params: type ? { type } : undefined,
			}));
		});
	}

	function getCancellationReasonTypes() {
		return getConfig('cancellation-reason-types');
	}

	function upsertCancellationReason(payload) {
		return upsertConfig('cancellation-reasons', payload, 'cancellationReasonId');
	}

	function deleteCancellationReason(id) {
		return deleteConfig('cancellation-reasons', id);
	}

	function getWaitlistReasons() {
		return getConfig('waitlist-reasons');
	}

	function upsertWaitlistReason(payload) {
		return upsertConfig('waitlist-reasons', payload, 'waitlistReasonId');
	}

	function deleteWaitlistReason(id) {
		return deleteConfig('waitlist-reasons', id);
	}

	function getCommunicationTriggers() {
		return getConfig('communication-triggers');
	}

	function upsertCommunicationTrigger(payload) {
		return upsertConfig('communication-triggers', payload, 'communicationTriggerId');
	}

	function getTemplateFields() {
		return getConfig('template-fields');
	}

	function getConfigLanguages() {
		return getConfig('languages');
	}

	function upsertLanguage(payload) {
		return upsertConfig('languages', payload, 'languageId');
	}

	function deleteLanguage(id) {
		return deleteConfig('languages', id);
	}

	function getConfigEthnicities() {
		return getConfig('ethnicities');
	}

	function upsertEthnicity(payload) {
		return upsertConfig('ethnicities', payload, 'ethnicityId');
	}

	function deleteEthnicity(id) {
		return deleteConfig('ethnicities', id);
	}

	function getConfigIwi() {
		return getConfig('iwi');
	}

	function upsertIwi(payload) {
		return upsertConfig('iwi', payload, 'iwiId');
	}

	function deleteIwi(id) {
		return deleteConfig('iwi', id);
	}

	function getIdentificationTypes() {
		return getConfig('identification-types');
	}

	function upsertIdentificationType(payload) {
		return upsertConfig('identification-types', payload, 'identificationTypeId');
	}

	function deleteIdentificationType(id) {
		return deleteConfig('identification-types', id);
	}

	function getConfigGenders() {
		return getConfig('genders');
	}

	function upsertGender(payload) {
		return upsertConfig('genders', payload, 'genderId');
	}

	function deleteGender(id) {
		return deleteConfig('genders', id);
	}

	function refreshConfigCache() {
		return buildRequest().then(function (req) {
			return makeRequest(req.post('/api/Config/cache/refresh'));
		});
	}

	return {
		refreshConfigCache,
		getIntents,
		upsertIntent,
		deleteIntent,
		getSources,
		upsertSource,
		deleteSource,
		getTags,
		upsertTag,
		deleteTag,
		getReferralSources,
		upsertReferralSource,
		deleteReferralSource,
		getOnHoldReasons,
		upsertOnHoldReason,
		deleteOnHoldReason,
		getStatuses,
		upsertStatus,
		deleteStatus,
		getChildAges,
		upsertChildAge,
		deleteChildAge,
		getSessions,
		upsertSession,
		deleteSession,
		getRelationships,
		upsertRelationship,
		deleteRelationship,
		getCommunicationTypes,
		upsertCommunicationType,
		deleteCommunicationType,
		getNoteTypes,
		upsertNoteType,
		deleteNoteType,
		getCancellationReasons,
		getCancellationReasonTypes,
		upsertCancellationReason,
		deleteCancellationReason,
		getWaitlistReasons,
		upsertWaitlistReason,
		deleteWaitlistReason,
		getCommunicationTriggers,
		upsertCommunicationTrigger,
		getTemplateFields,
		getConfigLanguages,
		upsertLanguage,
		deleteLanguage,
		getConfigEthnicities,
		upsertEthnicity,
		deleteEthnicity,
		getConfigIwi,
		upsertIwi,
		deleteIwi,
		getConfigGenders,
		upsertGender,
		deleteGender,
		getIdentificationTypes,
		upsertIdentificationType,
		deleteIdentificationType,
	};
}